import React from "react";
import ReactDOM from "react-dom";
import { MoreVertical, Edit, Trash2, Eye } from "lucide-react";
import { Customer } from "../../types/customer";
import { CustomerTypeBadge } from "../ui/CustomerTypeBadge";

interface CustomerTableProps {
  customers: Customer[];
  onEdit?: (customer: Customer) => void;
  onDelete?: (customerId: string) => void;
  onView?: (customer: Customer) => void;
  isLoading?: boolean;
}

export const CustomerTable: React.FC<CustomerTableProps> = ({
  customers,
  onEdit,
  onDelete,
  onView,
  isLoading = false,
}) => {
  const [openMenuId, setOpenMenuId] = React.useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = React.useState<{
    top: number;
    left: number;
  } | null>(null);
  const buttonRefs = React.useRef<{ [key: string]: HTMLButtonElement | null }>(
    {},
  );

  const handleMenuToggle = (
    customerId: string,
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    if (openMenuId === customerId) {
      setOpenMenuId(null);
      setDropdownPosition(null);
    } else {
      const button = event.currentTarget;
      const rect = button.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.right - 192 + window.scrollX, // 192px = w-48 in tailwind
      });
      setOpenMenuId(customerId);
    }
  };

  const handleAction = (action: () => void) => {
    action();
    setOpenMenuId(null);
    setDropdownPosition(null);
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        !target.closest(".dropdown-menu") &&
        !target.closest(".dropdown-trigger")
      ) {
        setOpenMenuId(null);
        setDropdownPosition(null);
      }
    };

    if (openMenuId) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [openMenuId]);

  if (isLoading) {
    return (
      <div className="bg-slate-800 rounded-lg overflow-hidden">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-slate-400">Loading customers...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-800 rounded-lg">
      {/* Desktop Table View */}
      <div className="hidden md:block">
        <div className="overflow-x-auto rounded-lg">
          <table className="w-full min-w-[640px]">
            <thead className="bg-slate-700">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Customer ID
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Country
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {customers.map((customer) => (
                <tr
                  key={customer.id}
                  className="hover:bg-slate-700 transition-colors cursor-pointer"
                  onClick={() => onView?.(customer)}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                    {customer.playerId || "—"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {customer.photo ? (
                        <img
                          className="h-10 w-10 rounded-full object-cover"
                          src={`${process.env.REACT_APP_API_BASE_URL}/${customer.photo}`}
                          alt={customer.name}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = "none";
                            target.nextElementSibling?.classList.remove(
                              "hidden",
                            );
                          }}
                        />
                      ) : null}
                      {/* Fallback initials avatar */}
                      <div
                        className={`h-10 w-10 rounded-full bg-slate-600 flex items-center justify-center text-sm font-medium text-white ${customer.photo ? "hidden" : ""}`}
                      >
                        {customer.name
                          .split(" ")
                          .map((n) => n[0])
                          .join("")
                          .toUpperCase()
                          .slice(0, 2)}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-white">
                          {customer.name}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-white">{customer.email}</div>
                    <div className="text-sm text-slate-400">
                      {customer.phone}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-300">
                    {customer.country || "Unknown"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <CustomerTypeBadge type={customer.customerType} />
                  </td>

                  <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-400">
                    <button
                      ref={(el) => {
                        buttonRefs.current[customer.id] = el;
                      }}
                      className="p-1 hover:bg-slate-600 rounded dropdown-trigger"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuToggle(customer.id, e);
                      }}
                    >
                      <MoreVertical className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden">
        <div className="divide-y divide-slate-700">
          {customers.map((customer) => (
            <div
              key={customer.id}
              className="p-4 hover:bg-slate-700 transition-colors cursor-pointer"
              onClick={() => onView?.(customer)}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center">
                  {customer.photo ? (
                    <img
                      className="h-12 w-12 rounded-full object-cover"
                      src={`${process.env.REACT_APP_API_BASE_URL}/${customer.photo}`}
                      alt={customer.name}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = "none";
                        target.nextElementSibling?.classList.remove("hidden");
                      }}
                    />
                  ) : null}
                  {/* Fallback initials avatar */}
                  <div
                    className={`h-12 w-12 rounded-full bg-slate-600 flex items-center justify-center text-sm font-medium text-white ${customer.photo ? "hidden" : ""}`}
                  >
                    {customer.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .toUpperCase()
                      .slice(0, 2)}
                  </div>
                  <div className="ml-3">
                    <div className="text-base font-medium text-white">
                      {customer.name}
                    </div>
                    <div className="text-xs text-slate-400">
                      ID: {customer.playerId || "—"}
                    </div>
                  </div>
                </div>
                <button
                  ref={(el) => {
                    buttonRefs.current[`mobile-${customer.id}`] = el;
                  }}
                  className="p-1 hover:bg-slate-600 rounded dropdown-trigger"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMenuToggle(customer.id, e);
                  }}
                >
                  <MoreVertical className="h-5 w-5 text-slate-400" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-slate-400">Email:</span>
                  <div className="text-white truncate">{customer.email}</div>
                </div>
                <div>
                  <span className="text-slate-400">Phone:</span>
                  <div className="text-white">{customer.phone}</div>
                </div>
                <div>
                  <span className="text-slate-400">Country:</span>
                  <div className="text-white">
                    {customer.country || "Unknown"}
                  </div>
                </div>
                <div>
                  <span className="text-slate-400">Type:</span>
                  <div className="mt-1">
                    <CustomerTypeBadge type={customer.customerType} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {customers.length === 0 && !isLoading && (
        <div className="text-center py-12">
          <div className="text-slate-400">No customers found</div>
        </div>
      )}

      {/* Portal-rendered Dropdown Menu */}
      {openMenuId &&
        dropdownPosition &&
        ReactDOM.createPortal(
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => {
                setOpenMenuId(null);
                setDropdownPosition(null);
              }}
            />
            <div
              className="dropdown-menu fixed w-48 bg-slate-700 rounded-md shadow-lg z-50 border border-slate-600"
              style={{
                top: `${dropdownPosition.top}px`,
                left: `${dropdownPosition.left}px`,
              }}
            >
              <div className="py-1">
                {onView && (
                  <button
                    className="flex items-center w-full px-4 py-2 text-sm text-slate-300 hover:bg-slate-600 hover:text-white"
                    onClick={(e) => {
                      e.stopPropagation();
                      const customer = customers.find(
                        (c) => c.id === openMenuId,
                      );
                      if (customer) {
                        handleAction(() => onView(customer));
                      }
                    }}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View Details
                  </button>
                )}
                {onEdit && (
                  <button
                    className="flex items-center w-full px-4 py-2 text-sm text-slate-300 hover:bg-slate-600 hover:text-white"
                    onClick={(e) => {
                      e.stopPropagation();
                      const customer = customers.find(
                        (c) => c.id === openMenuId,
                      );
                      if (customer) {
                        handleAction(() => onEdit(customer));
                      }
                    }}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit Customer
                  </button>
                )}
                {onDelete && (
                  <button
                    className="flex items-center w-full px-4 py-2 text-sm text-red-300 hover:bg-red-600 hover:text-white"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAction(() => onDelete(openMenuId));
                    }}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Customer
                  </button>
                )}
              </div>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
};
